import { EntryCollection } from "@/types/entryCollection";
import { getStoppedTimerMs } from "@/utils/entryUtils";

/**
 * Calculate statistics for a specific trait from entry collections
 */
export function getTraitStats(entryCollections: EntryCollection[], traitId: string) {
  // Filter collections to only those for the selected trait
  const selectedTraitCollections = entryCollections.filter((collection) => {
    const collectionTraitId =
      typeof collection.entryConfigRef === "string"
        ? collection.entryConfigRef
        : collection.entryConfigRef?.id;
    return collectionTraitId === traitId;
  });

  // Get unique sample keys across all collections for the selected trait
  const uniqueSamples = new Set<string>();
  selectedTraitCollections.forEach((collection) => {
    if (!collection.type) return;

    // A photo "sample" only counts if it actually holds an image. Empty slots —
    // left behind by a clear-in-place photo delete or by "Add Sample" — are
    // photoEntries nodes with no imgCloudPath, and must not inflate the count
    // (mirrors getPhotoStats below).
    if (collection.type === "photo") {
      Object.entries(collection.photoEntries ?? {}).forEach(([key, entry]) => {
        if (entry?.imgCloudPath) uniqueSamples.add(key);
      });
      return;
    }

    // A timer "sample" only counts once it is stopped (elapsedMs > 0). Idle
    // (elapsedMs absent/0) and running (startedAtEpochMs only) slots must not
    // inflate the count (mirrors the display/export/collected predicate).
    if (collection.type === "timer") {
      Object.entries(collection.timerEntries ?? {}).forEach(([key, entry]) => {
        if (getStoppedTimerMs(entry) != null) uniqueSamples.add(key);
      });
      return;
    }

    const entryMap = {
      text: collection.textEntries,
      number: collection.numberEntries,
      category: collection.categoryEntries,
      date: collection.dateEntries,
      transcription: collection.transcriptionEntries,
      photo: collection.photoEntries,
      boolean: collection.booleanEntries,
      percentage: collection.percentageEntries,
      leafAreaIndex: collection.leafAreaIndexEntries,
      ruler: collection.rulerEntries,
      imageMeasurement: collection.imageMeasurementEntries,
      standCountMeasurement: collection.standCountMeasurementEntries,
      rowPlantedStandCount: collection.rowPlantedStandCountEntries,
    }[collection.type];

    if (entryMap) {
      Object.keys(entryMap).forEach((key) => uniqueSamples.add(key));
    }
  });

  return {
    sampleCount: uniqueSamples.size,
    collectionCount: selectedTraitCollections.length,
  };
}

/**
 * Get photo-specific statistics for a trait from entry collections
 * Returns photo count and unique contributor count
 */
export function getPhotoStats(entryCollections: EntryCollection[], traitId: string) {
  // Filter collections to only those for the selected trait
  const matchingCollections = entryCollections.filter((collection) => {
    const collectionTraitId =
      typeof collection.entryConfigRef === "string"
        ? collection.entryConfigRef
        : collection.entryConfigRef?.id;
    return collectionTraitId === traitId;
  });

  let photoCount = 0;
  const uniqueContributors = new Set<string>();

  matchingCollections.forEach((collection) => {
    if (collection.photoEntries) {
      const photos = Object.values(collection.photoEntries).filter(e => e?.imgCloudPath);
      if (photos.length > 0) {
        photoCount += photos.length;
        // Get contributor ID from createdBy DocumentReference
        const contributorId = typeof collection.createdBy === 'string'
          ? collection.createdBy
          : collection.createdBy?.id;
        if (contributorId) {
          uniqueContributors.add(contributorId);
        }
      }
    }
  });

  return {
    photoCount,
    contributorCount: uniqueContributors.size,
  };
}
